import React, { useRef } from 'react';
import styled from 'styled-components';
import { VscArrowDown, VscGlobe, VscSmiley } from 'react-icons/vsc';
import { FaKeyboard } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';

import { mobileMediaQuery, themeColor } from '../../Theming';
import { Flex } from '../../Common/Layouts';
import { useDisclosure, useScrollTo } from '../../Common/Hooks';
import DescriptionModal from './DescriptionModal';
import { StyledButton1 } from '../../Common/Buttons/StyledButtons';
import { isAndroid } from '../../Common/Utils/platforms';
import images from '../../Theming/images';

const Container = styled.div`
  align-items: center;
  width: 100%;
  max-width: 700px;
  padding: 0 1.5rem;
  position: relative;
  font-size: small;
  h4 {
    font-size: 1rem;
    text-align: left;
  }
  p {
    color: ${({ color }) => color};
  }
  ul {
    color: ${({ color }) => color};
    padding: 0;
    padding-left: 1rem;
    li:first-child {
      margin-left: -1rem;
      list-style: none;
    }
  }
  button {
    margin: 1rem 0;
  }
  ${mobileMediaQuery} {
    width: 100%;
    h4 {
      text-align: center;
    }
    button {
      width: 100%;
    }
  }
  span {
    margin: 0 0.5rem;
    display: inline;
  }
`;
const BoldParagraph = styled.p`
  font-weight: bold;
  text-align: left;
  width: 100%;
  margin: 1rem 0;
  ${mobileMediaQuery} {
    text-align: center;
  }
`;
const ModalButton = styled.p`
  text-decoration: underline;
  color: gray;
  display: inline-block;
  cursor: pointer;
  font-size: x-small;
  min-width: fit-content;
  align-self: flex-end;
`;

const ImageContainer = styled.div`
  max-width: fit-content;
  position: relative;
  input[type='text'] {
    opacity: 0;
    position: absolute;
    top: 0;
    left: 0;
  }
  p {
    outline: none;
    position: absolute;
    width: ${isAndroid ? '8rem' : '4.1rem'};
    left: ${isAndroid ? '4rem' : '6.75rem'};
    top: ${isAndroid ? '5.4rem' : '5.6rem'};
    border: none;
    color: ${isAndroid ? 'black' : 'white'};
    font-size: small;
    text-align: ${isAndroid ? 'left' : 'center'};
    background-color: ${isAndroid ? 'white' : themeColor('customBlue')};
    padding: ${isAndroid ? '.1rem .3rem' : '0'};
    ${mobileMediaQuery} {
      padding: ${isAndroid ? '.1rem .2rem' : '0'};
      top: ${isAndroid ? '5rem' : '5.4rem'};
      width: ${isAndroid ? '8rem' : '3.8rem'};
    }
  }
  img {
    width: 20rem;
    object-fit: contain;
    ${mobileMediaQuery} {
      margin: auto;
      width: 100%;
    }
  }
  ${mobileMediaQuery} {
    margin: auto;
    width: 20rem;
  }
`;

const Description = () => {
  const { t } = useTranslation();
  const { isOpen, onClose, onOpen } = useDisclosure();
  const hiddenInput = useRef<HTMLInputElement>(null);
  const imgContainerRef = useRef<HTMLDivElement>(null);
  const scrollTo = useScrollTo(imgContainerRef);

  const activateKeyboard = () => {
    hiddenInput?.current?.focus();
    scrollTo();
  };

  return (
    <>
      <DescriptionModal show={isOpen} onHide={onClose} />
      <Container color="gray">
        <h4>{t('keyboardInstallationGuide.heading1')}</h4>
        {isAndroid ? (
          <p>{t('keyboardInstallationGuide.androidGuide')}</p>
        ) : (
          <>
            <ul>
              <li>{t('keyboardInstallationGuide.iosGuideLi1')}</li>
              <li>{t('keyboardInstallationGuide.iosGuideLi2')}</li>
              <li> {t('keyboardInstallationGuide.iosGuideLi3')}</li>
            </ul>
            <ModalButton onClick={onOpen}>
              {t('keyboardInstallationGuide.WhatSFullAccess')}
            </ModalButton>
          </>
        )}
      </Container>
      <Container>
        <h4>{t('keyboardInstallationGuide.heading2')}</h4>
        <Flex align="center">
          <Flex align="center" width={45}>
            <VscArrowDown size={36} />
          </Flex>
          <p>
            {t('keyboardInstallationGuide.pPart1')}
            {isAndroid ? (
              <span>
                <FaKeyboard />
              </span>
            ) : (
              <>
                <span>
                  <VscGlobe />
                </span>
                or
                <span>
                  <VscSmiley />
                </span>
              </>
            )}
            {t('keyboardInstallationGuide.pPart2')}
          </p>
        </Flex>

        <StyledButton1 onClick={activateKeyboard}>
          {t('keyboardInstallationGuide.activateBtnContent')}
        </StyledButton1>
      </Container>
      <Container ref={imgContainerRef}>
        <ImageContainer>
          <input type="text" defaultValue="Kindred" ref={hiddenInput} />
          <img
            src={
              isAndroid
                ? images.keyboard_installation_guide_android?.default
                : images.keyboard_installation_guide_ios?.default
            }
            alt="Activate keyboard"
          />
          {/* Dynamic query paramater goes here */}
          <p>Kindred</p>
        </ImageContainer>
        <BoldParagraph>{t('keyboardInstallationGuide.footer')}</BoldParagraph>
      </Container>
    </>
  );
};

export default Description;
